import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Pickaxe, Layers, FileScan, ShieldCheck, Globe, UploadCloud, GitBranch,
  AlertTriangle, Users, ClipboardCheck, Rocket, Sparkles, Loader2, Plus,
  CheckCircle2, XCircle, PlayCircle, RotateCcw, MapPin, Search,
} from "lucide-react";

const TABS = [
  { key: "intake", label: "Legacy Claim Intake", icon: Layers },
  { key: "extraction", label: "Coordinate Extraction", icon: FileScan },
  { key: "validation", label: "Validation Engine", icon: ShieldCheck },
  { key: "crs", label: "CRS Management", icon: Globe },
  { key: "imports", label: "Bulk Import & ETL", icon: UploadCloud },
  { key: "mapping", label: "Field Mapping", icon: GitBranch },
  { key: "conflicts", label: "Conflicts", icon: AlertTriangle },
  { key: "exceptions", label: "Exceptions", icon: ClipboardCheck },
  { key: "workbench", label: "Officer Workbench", icon: Users },
  { key: "quality", label: "Data Quality", icon: Sparkles },
  { key: "go-live", label: "Progressive Go-Live", icon: Rocket },
];

const KPI = ({ label, value, hint, testid }) => (
  <div className="cad-panel p-4" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
    {hint && <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">{hint}</div>}
  </div>
);

const SAMPLE_TEXT = `SG plan 4128/1994. Beacon schedule for Special Grant SG/1994/218. Surveyor: T. Chikwanha.
E: 526432.12  N: 7823145.88  BM-A1  bearing 042°15'22"  distance 128.4m
E: 526560.55  N: 7823130.14  BM-A2  bearing 048°01'10"  distance 132.9m
E: 526612.32  N: 7823025.66  BM-A3
E: 526458.19  N: 7822998.31  BM-A4
Area: 4.82 ha. CRS: Arc 1950 UTM 36S.`;

const STATUS_COLOR = {
  draft: "cad-chip-blue", submitted: "cad-chip-blue", intake: "cad-chip-blue",
  extraction: "cad-chip-blue", validation: "cad-chip-blue",
  conflict_review: "cad-chip-gold", officer_review: "cad-chip-gold",
  correction_requested: "cad-chip-gold",
  approved: "cad-chip-green", committed: "cad-chip-green",
  rejected: "cad-chip-red",
};

export const MiningMigrationCentre = () => {
  const qc = useQueryClient();
  const [tab, setTab] = useState("intake");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ status: "", province: "", commodity: "", q: "" });
  const [extractText, setExtractText] = useState(SAMPLE_TEXT);
  const [extractResult, setExtractResult] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [newSub, setNewSub] = useState({
    claim_name: "", claim_type: "Mining Claim", commodity: "Gold",
    claimant_name: "", province: "Matabeleland South", district: "",
    source_type: "paper_intake",
  });
  const [newImport, setNewImport] = useState({
    source_format: "shapefile", file_name: "", province: "Matabeleland South", notes: "",
  });

  const ref = useQuery({ queryKey: ["mm-ref"], queryFn: async () => (await api.get("/cadastre360/migration/reference")).data });
  const submissions = useQuery({
    queryKey: ["mm-subs", filter],
    queryFn: async () => (await api.get("/cadastre360/migration/submissions", { params: {
      status: filter.status || undefined, province: filter.province || undefined,
      commodity: filter.commodity || undefined, q: filter.q || undefined, limit: 60,
    }})).data,
  });
  const progress = useQuery({ queryKey: ["mm-progress"], queryFn: async () => (await api.get("/cadastre360/migration/progress")).data });
  const conflicts = useQuery({ queryKey: ["mm-conflicts"], queryFn: async () => (await api.get("/cadastre360/migration/conflicts")).data });
  const exceptions = useQuery({ queryKey: ["mm-exceptions"], queryFn: async () => (await api.get("/cadastre360/migration/exceptions")).data });
  const imports = useQuery({ queryKey: ["mm-imports"], queryFn: async () => (await api.get("/cadastre360/migration/imports")).data });
  const mappings = useQuery({ queryKey: ["mm-mappings"], queryFn: async () => (await api.get("/cadastre360/migration/mappings")).data });
  const dq = useQuery({ queryKey: ["mm-dq"], queryFn: async () => (await api.get("/cadastre360/migration/data-quality")).data });
  const goLive = useQuery({ queryKey: ["mm-go-live"], queryFn: async () => (await api.get("/cadastre360/migration/go-live")).data });
  const detail = useQuery({
    queryKey: ["mm-detail", selectedSubmission],
    queryFn: async () => (await api.get(`/cadastre360/migration/submissions/${selectedSubmission}`)).data,
    enabled: !!selectedSubmission,
  });

  const createSub = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/migration/submissions", newSub)).data,
    onSuccess: () => {
      toast.success("Claim intake staged");
      qc.invalidateQueries({ queryKey: ["mm-subs"] });
      qc.invalidateQueries({ queryKey: ["mm-progress"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const createImport = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/migration/imports", newImport)).data,
    onSuccess: () => { toast.success("Import staged"); qc.invalidateQueries({ queryKey: ["mm-imports"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const runExtract = async () => {
    setBusy(true); setExtractResult(null);
    try {
      const { data } = await api.post("/cadastre360/migration/extraction", { text: extractText, source_document: "beacon_schedule" });
      setExtractResult(data);
      toast.success(`Extraction complete (${Math.round(data.overall_confidence * 100)}% confidence)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runValidation = async (sid) => {
    setBusy(true);
    try {
      const { data } = await api.post("/cadastre360/migration/validate", { submission_id: sid });
      toast.success(`Validation ${data.outcome}`);
      qc.invalidateQueries({ queryKey: ["mm-subs"] });
      qc.invalidateQueries({ queryKey: ["mm-detail", sid] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recordReview = async (sid, decision, note = "") => {
    try {
      await api.post("/cadastre360/migration/reviews", { submission_id: sid, decision, note });
      toast.success(`Recorded: ${decision.replace(/_/g, " ")}`);
      qc.invalidateQueries({ queryKey: ["mm-subs"] });
      qc.invalidateQueries({ queryKey: ["mm-detail", sid] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const commitSubmission = async (sid) => {
    try {
      await api.post(`/cadastre360/migration/commit/${sid}`);
      toast.success("Submission committed to register");
      qc.invalidateQueries({ queryKey: ["mm-subs"] });
      qc.invalidateQueries({ queryKey: ["mm-progress"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveMapping = async (mid) => {
    try {
      await api.post(`/cadastre360/migration/mappings/${mid}/approve`);
      toast.success("Mapping approved");
      qc.invalidateQueries({ queryKey: ["mm-mappings"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setGoLive = async (province, status) => {
    try {
      await api.post("/cadastre360/migration/go-live", { province, status });
      toast.success(`${province} → ${status.replace(/_/g, " ")}`);
      qc.invalidateQueries({ queryKey: ["mm-go-live"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const advanceImport = async (iid) => {
    try {
      await api.post(`/cadastre360/migration/imports/${iid}/advance`);
      qc.invalidateQueries({ queryKey: ["mm-imports"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rollbackImport = async (iid) => {
    try {
      await api.post(`/cadastre360/migration/imports/${iid}/rollback`);
      toast.success("Import rolled back");
      qc.invalidateQueries({ queryKey: ["mm-imports"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const p = progress.data;
  const dqd = dq.data;

  return (
    <div className="space-y-6" data-testid="page-mining-migration">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">
          Cadastre360™ — Mining Cadastre Migration Acceleration Layer
        </div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Pickaxe className="text-[#C99A2E]" /> Mining Data Migration
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Additive acceleration layer for historical mining claim records, surveyor coordinate submissions and legacy register migration.
          Feature flag: <span className="cad-chip cad-chip-gold text-[10px] uppercase">{ref.data?.feature_flag || "cadastre360_mining_migration_accelerator"}</span>
          {ref.data && (ref.data.enabled_for_user
            ? <span className="cad-chip cad-chip-green text-[10px] uppercase ml-1">enabled for you</span>
            : <span className="cad-chip cad-chip-red text-[10px] uppercase ml-1">not enabled</span>)}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Expected" value={(p?.total_expected ?? 0).toLocaleString()} testid="mm-kpi-expected" />
        <KPI label="Submitted" value={(p?.submissions_received ?? 0).toLocaleString()} testid="mm-kpi-submitted" />
        <KPI label="Validated" value={(p?.records_validated ?? 0).toLocaleString()} testid="mm-kpi-validated" />
        <KPI label="Committed" value={(p?.records_committed ?? 0).toLocaleString()} hint={`${p?.completion_pct ?? 0}%`} testid="mm-kpi-committed" />
        <KPI label="Rejected" value={(p?.rejected ?? 0).toLocaleString()} testid="mm-kpi-rejected" />
        <KPI label="Projected ETA" value={p?.projected_eta ?? "—"} hint={`${p?.weekly_throughput ?? 0}/wk`} testid="mm-kpi-eta" />
      </div>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1" data-testid="mm-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mm-tab-${t.key}`}
            className={`px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5
                        ${tab === t.key ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
            <t.icon size={12} className="text-[#C99A2E]" /> {t.label}
          </button>
        ))}
      </div>

      {/* INTAKE */}
      {tab === "intake" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mm-intake">
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Legacy claim submissions</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">All submissions remain isolated in staging until formally approved and committed.</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2.5 text-[#7C93A5]" />
                <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="mm-search"
                       placeholder="Search claim, ref or claimant"
                       className="bg-[#071B2B] border cad-hairline rounded-sm pl-7 pr-2 py-1.5 text-[12px] text-[#F6F8FA] w-64" />
              </div>
              <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="mm-filter-status"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-1.5 text-[12px] text-[#F6F8FA]">
                <option value="">All statuses</option>
                {(ref.data?.submission_statuses || []).map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
              </select>
              <select value={filter.province} onChange={(e) => setFilter({ ...filter, province: e.target.value })} data-testid="mm-filter-province"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-1.5 text-[12px] text-[#F6F8FA]">
                <option value="">All provinces</option>
                {(ref.data?.provinces || []).map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              <select value={filter.commodity} onChange={(e) => setFilter({ ...filter, commodity: e.target.value })} data-testid="mm-filter-commodity"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-1.5 text-[12px] text-[#F6F8FA]">
                <option value="">All commodities</option>
                {(ref.data?.commodities || []).map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>

            <div className="mt-3 max-h-[440px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead><tr className="text-left border-b cad-hairline sticky top-0 bg-[#0B3558]">
                  {["Migration Ref", "Claim", "Commodity", "Province", "Claimant", "Status", "Conf"].map((h) => (
                    <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="cad-tbody">
                  {(submissions.data || []).map((s) => (
                    <tr key={s.id} className="cursor-pointer" onClick={() => setSelectedSubmission(s.id)} data-testid={`mm-sub-${s.id}`}>
                      <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{s.migration_reference}</td>
                      <td className="px-2 py-1.5 text-[#DCE3E8]">{s.claim_name}</td>
                      <td className="px-2 py-1.5 text-[#B3C2CC]">{s.commodity}</td>
                      <td className="px-2 py-1.5 text-[#B3C2CC]">{s.province}</td>
                      <td className="px-2 py-1.5 text-[#B3C2CC] truncate max-w-[140px]">{s.claimant_name}</td>
                      <td className="px-2 py-1.5"><span className={`cad-chip ${STATUS_COLOR[s.status] || "cad-chip-blue"} text-[9px] uppercase`}>{s.status.replace(/_/g, " ")}</span></td>
                      <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{Math.round((s.validation_confidence || 0) * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New legacy intake</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Create a controlled intake record. Documents can be attached from the workbench.</div>
            <div className="space-y-2 mt-3">
              <input value={newSub.claim_name} onChange={(e) => setNewSub({ ...newSub, claim_name: e.target.value })}
                     placeholder="Claim name (e.g. Gold Mining Claim 42)" data-testid="mm-new-name"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <input value={newSub.claimant_name} onChange={(e) => setNewSub({ ...newSub, claimant_name: e.target.value })}
                     placeholder="Claimant name" data-testid="mm-new-claimant"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <div className="grid grid-cols-2 gap-2">
                <select value={newSub.claim_type} onChange={(e) => setNewSub({ ...newSub, claim_type: e.target.value })} data-testid="mm-new-type"
                        className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                  {(ref.data?.claim_types || []).map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
                <select value={newSub.commodity} onChange={(e) => setNewSub({ ...newSub, commodity: e.target.value })} data-testid="mm-new-commodity"
                        className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                  {(ref.data?.commodities || []).map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <select value={newSub.province} onChange={(e) => setNewSub({ ...newSub, province: e.target.value })} data-testid="mm-new-province"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.provinces || []).map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
              <input value={newSub.district} onChange={(e) => setNewSub({ ...newSub, district: e.target.value })}
                     placeholder="District (optional)" data-testid="mm-new-district"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <button onClick={() => createSub.mutate()} disabled={!newSub.claim_name || !newSub.claimant_name || createSub.isPending}
                      data-testid="mm-new-submit"
                      className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                {createSub.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Stage intake
              </button>
            </div>
          </div>

          {/* Submission detail */}
          {selectedSubmission && detail.data && (
            <div className="cad-panel p-5 lg:col-span-3" data-testid="mm-detail">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Submission detail</div>
                  <div className="font-display font-bold text-[#F6F8FA] text-xl mt-1">{detail.data.submission.claim_name}</div>
                  <div className="text-[12px] text-[#B3C2CC]">
                    {detail.data.submission.migration_reference} · {detail.data.submission.commodity} · {detail.data.submission.province}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => runValidation(selectedSubmission)} disabled={busy} data-testid="mm-detail-validate"
                          className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
                    <ShieldCheck size={11} /> Run Validation
                  </button>
                  <button onClick={() => recordReview(selectedSubmission, "approve_metadata")} data-testid="mm-detail-approve-meta"
                          className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm">Approve Metadata</button>
                  <button onClick={() => recordReview(selectedSubmission, "approve_geometry")} data-testid="mm-detail-approve-geom"
                          className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm">Approve Geometry</button>
                  <button onClick={() => recordReview(selectedSubmission, "request_correction", "Please resubmit corrections")}
                          data-testid="mm-detail-request-correction"
                          className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm">Request Correction</button>
                  <button onClick={() => recordReview(selectedSubmission, "approve")} data-testid="mm-detail-approve"
                          className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"><CheckCircle2 size={11} /> Approve</button>
                  <button onClick={() => commitSubmission(selectedSubmission)} data-testid="mm-detail-commit"
                          className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"><PlayCircle size={11} /> Commit</button>
                  <button onClick={() => recordReview(selectedSubmission, "reject", "Fails minimum criteria")}
                          data-testid="mm-detail-reject"
                          className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"><XCircle size={11} /> Reject</button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">Documents ({detail.data.documents.length})</div>
                  <ul className="mt-2 space-y-1 text-[12px]">
                    {detail.data.documents.slice(0, 5).map((d) => (
                      <li key={d.id} className="text-[#DCE3E8] truncate">{d.kind.replace(/_/g, " ")} · {d.file_name}</li>
                    ))}
                    {detail.data.documents.length === 0 && <li className="text-[#7C93A5] italic">No documents</li>}
                  </ul>
                </div>
                <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">Coordinates ({detail.data.coordinates.length})</div>
                  <div className="text-[11px] text-[#DCE3E8] mt-2 font-mono-cad max-h-40 overflow-y-auto">
                    {detail.data.coordinates.slice(0, 8).map((c) => (
                      <div key={c.id}>#{c.vertex_index + 1} · {c.beacon_ref} · E{Math.round(c.source_easting)} N{Math.round(c.source_northing)}</div>
                    ))}
                  </div>
                </div>
                <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">Validation</div>
                  {detail.data.validation ? (
                    <div className="mt-2">
                      <span className={`cad-chip ${detail.data.validation.outcome === "pass" ? "cad-chip-green" : detail.data.validation.outcome === "fail" ? "cad-chip-red" : "cad-chip-gold"} text-[10px] uppercase`}>
                        {detail.data.validation.outcome}
                      </span>
                      <div className="text-[11px] text-[#B3C2CC] mt-2">Confidence {Math.round((detail.data.validation.confidence || 0) * 100)}%</div>
                    </div>
                  ) : (<div className="text-[11px] text-[#7C93A5] italic mt-2">Not yet validated</div>)}
                </div>
              </div>

              {(detail.data.exceptions?.length > 0 || detail.data.conflicts?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {detail.data.exceptions?.length > 0 && (
                    <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                      <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#E67E7E]">Exceptions</div>
                      <ul className="mt-2 space-y-1 text-[12px]">
                        {detail.data.exceptions.map((x) => (
                          <li key={x.id} className="text-[#F6F8FA]">
                            <span className="cad-chip cad-chip-red text-[9px] uppercase mr-1">{x.queue.replace(/_/g, " ")}</span>{x.note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {detail.data.conflicts?.length > 0 && (
                    <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                      <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#C99A2E]">Conflicts</div>
                      <ul className="mt-2 space-y-1 text-[12px]">
                        {detail.data.conflicts.map((c) => (
                          <li key={c.id} className="text-[#F6F8FA]">
                            <span className="cad-chip cad-chip-gold text-[9px] uppercase mr-1">{c.type.replace(/_/g, " ")}</span>
                            {c.overlap_pct}% overlap vs {c.against_reference}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* EXTRACTION */}
      {tab === "extraction" && (
        <div className="cad-panel p-5" data-testid="mm-extraction">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI-assisted coordinate extraction</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Paste OCR text from a beacon schedule or survey diagram. Officers verify — never overwrite silently.</div>
          <textarea value={extractText} onChange={(e) => setExtractText(e.target.value)} rows={8} data-testid="mm-extract-input"
                    className="mt-3 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[12px] text-[#F6F8FA] font-mono-cad" />
          <button onClick={runExtract} disabled={busy || !extractText.trim()} data-testid="mm-extract-run"
                  className="cad-btn-primary mt-3 px-4 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <FileScan size={13} />} Extract for verification
          </button>

          {extractResult && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mm-extract-result">
              <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#C99A2E]">Coordinate vertices</div>
                <div className="text-[11px] mt-2 font-mono-cad text-[#DCE3E8] space-y-1">
                  {extractResult.extracted.eastings.map((e, i) => (
                    <div key={i}>#{i + 1} E {e.value} · N {extractResult.extracted.northings[i]?.value || "—"} · conf {Math.round(e.confidence * 100)}%</div>
                  ))}
                  {extractResult.extracted.eastings.length === 0 && <div className="italic text-[#7C93A5]">No coordinates detected</div>}
                </div>
              </div>
              <div className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#C99A2E]">Attributes</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-[12px]">
                  {["declared_crs", "declared_area", "bearing", "distance", "plan_number", "surveyor_name", "survey_date"].map((k) => (
                    <div key={k}>
                      <div className="font-mono-cad text-[9px] uppercase text-[#7C93A5]">{k.replace(/_/g, " ")}</div>
                      <div className="text-[#F6F8FA]">{extractResult.extracted[k]?.value || "—"}</div>
                      <div className="text-[9px] text-[#C99A2E] font-mono-cad">conf {Math.round((extractResult.extracted[k]?.confidence || 0) * 100)}%</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-[#7C93A5] italic">Beacons: {extractResult.extracted.beacon_references.map((b) => b.value).join(", ") || "—"}</div>
              </div>
              <div className="md:col-span-2 text-[11px] text-[#B3C2CC] italic">{extractResult.notes}</div>
            </div>
          )}
        </div>
      )}

      {/* VALIDATION */}
      {tab === "validation" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mm-validation">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Validation posture</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {["pass", "warning", "fail", "manual_review"].map((k) => (
                <div key={k} className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">{k.replace(/_/g, " ")}</div>
                  <div className="text-2xl font-display font-bold mt-1"
                    style={{color: k === "pass" ? "#6BB88A" : k === "warning" ? "#C99A2E" : k === "fail" ? "#E67E7E" : "#7CBFDC"}}>
                    {dqd?.outcomes?.[k] ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Automated checks executed</div>
            <ul className="mt-3 space-y-1.5 text-[12.5px] text-[#DCE3E8]">
              {["Correct coordinate sequence", "Polygon closure", "Duplicate vertices", "Missing vertices", "Invalid coordinates",
                "Self-intersection", "Overlapping geometry", "Zero-area polygons", "Unrealistic area", "Incorrect CRS",
                "Unit of measurement", "Outside jurisdiction", "Duplicate claim geometry", "Proximity to existing claims",
                "Declared vs calculated area mismatch"].map((x) => (
                <li key={x} className="flex items-center gap-2"><CheckCircle2 size={12} className="text-[#6BB88A]" />{x}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* CRS */}
      {tab === "crs" && (
        <div className="cad-panel p-5" data-testid="mm-crs">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Coordinate Reference System catalogue</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Target national CRS: <span className="cad-chip cad-chip-green text-[10px]">EPSG:4326</span> · original coordinates are always preserved.</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["EPSG", "Name"].map((h) => (
                <th key={h} className="px-3 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(ref.data?.crs_catalogue || []).map((c) => (
                <tr key={c.code} data-testid={`mm-crs-${c.code.replace(/:/g, "-")}`}>
                  <td className="px-3 py-1.5 font-mono-cad text-[#F6F8FA]">{c.code}</td>
                  <td className="px-3 py-1.5 text-[#DCE3E8]">{c.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* IMPORTS */}
      {tab === "imports" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mm-imports">
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Bulk import batches</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Batch", "Format", "Province", "Records", "Duplicates", "Quality", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(imports.data || []).map((b) => (
                  <tr key={b.id} data-testid={`mm-import-${b.id}`}>
                    <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{b.batch_no}</td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-blue text-[9px] uppercase">{b.source_format}</span></td>
                    <td className="px-2 py-1.5 text-[#B3C2CC]">{b.province}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{b.records_detected}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{b.duplicates_flagged}</td>
                    <td className={`px-2 py-1.5 font-mono-cad ${b.quality_score >= 85 ? "text-[#6BB88A]" : b.quality_score >= 65 ? "text-[#C99A2E]" : "text-[#E67E7E]"}`}>{b.quality_score}</td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{b.status.replace(/_/g, " ")}</span></td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => advanceImport(b.id)} data-testid={`mm-import-advance-${b.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm mr-1">Advance</button>
                      <button onClick={() => rollbackImport(b.id)} data-testid={`mm-import-rollback-${b.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                        <RotateCcw size={9} /> Rollback
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New bulk import</div>
            <div className="space-y-2 mt-3">
              <select value={newImport.source_format} onChange={(e) => setNewImport({ ...newImport, source_format: e.target.value })} data-testid="mm-import-format"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.supported_formats || []).map((f) => (<option key={f} value={f}>{f.toUpperCase().replace(/_/g, " ")}</option>))}
              </select>
              <select value={newImport.province} onChange={(e) => setNewImport({ ...newImport, province: e.target.value })} data-testid="mm-import-province"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.provinces || []).map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
              <input value={newImport.file_name} onChange={(e) => setNewImport({ ...newImport, file_name: e.target.value })}
                     placeholder="Source file name" data-testid="mm-import-file"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <button onClick={() => createImport.mutate()} disabled={!newImport.file_name || createImport.isPending}
                      data-testid="mm-import-submit"
                      className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                {createImport.isPending ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} Stage import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAPPINGS */}
      {tab === "mapping" && (
        <div className="cad-panel p-5" data-testid="mm-mapping">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI-assisted field mappings</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Recommendations require officer approval before commit. Automatic mapping never commits data.</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Legacy field", "Canonical field", "Confidence", "Approved by", "Action"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(mappings.data || []).map((m) => (
                <tr key={m.id} data-testid={`mm-map-${m.id}`}>
                  <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{m.legacy_field}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">→ {m.canonical_field}</td>
                  <td className="px-2 py-1.5 font-mono-cad">{Math.round(m.confidence * 100)}%</td>
                  <td className="px-2 py-1.5 text-[11px] text-[#B3C2CC]">{m.approved_by || "—"}</td>
                  <td className="px-2 py-1.5">
                    {!m.approved_by && (
                      <button onClick={() => approveMapping(m.id)} data-testid={`mm-map-approve-${m.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm">Approve</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CONFLICTS */}
      {tab === "conflicts" && (
        <div className="cad-panel p-5" data-testid="mm-conflicts">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Detected conflicts</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Type", "Overlap %", "Against reference", "Resolution", "Detected"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(conflicts.data || []).map((c) => (
                <tr key={c.id} data-testid={`mm-conflict-${c.id}`}>
                  <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{c.type.replace(/_/g, " ")}</span></td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{c.overlap_pct}%</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{c.against_reference}</td>
                  <td className="px-2 py-1.5 text-[#DCE3E8]">{c.resolution.replace(/_/g, " ")}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[10px] text-[#B3C2CC]">{c.detected_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EXCEPTIONS */}
      {tab === "exceptions" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mm-exceptions">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Exception queues</div>
            <div className="mt-3 space-y-2 text-[12.5px]">
              {Object.entries(exceptions.data?.by_queue || {}).map(([q, n]) => (
                <div key={q} className="flex items-center justify-between border cad-hairline rounded-sm px-3 py-2 bg-[#071B2B]" data-testid={`mm-queue-${q}`}>
                  <span className="text-[#F6F8FA]">{q.replace(/_/g, " ")}</span>
                  <span className="cad-chip cad-chip-gold text-[10px]">{n}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Open exceptions</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Queue", "Note", "Assigned", "Status", "Created"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(exceptions.data?.rows || []).slice(0, 40).map((x) => (
                  <tr key={x.id}>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-red text-[9px] uppercase">{x.queue.replace(/_/g, " ")}</span></td>
                    <td className="px-2 py-1.5 text-[#DCE3E8]">{x.note}</td>
                    <td className="px-2 py-1.5 text-[#B3C2CC] text-[11px]">{x.assigned_to}</td>
                    <td className="px-2 py-1.5 text-[#DCE3E8]">{x.status}</td>
                    <td className="px-2 py-1.5 text-[11px] text-[#B3C2CC]">{x.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* WORKBENCH */}
      {tab === "workbench" && (
        <div className="cad-panel p-5" data-testid="mm-workbench">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Officer review workbench</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Select a submission on the Intake tab to see the full workbench: documents, coordinates, validation, exceptions, conflicts and controlled decision actions.</div>
          <div className="mt-3 text-[12.5px] text-[#DCE3E8]">Available controlled actions:</div>
          <ul className="text-[12.5px] mt-2 space-y-1 text-[#DCE3E8]">
            {["Request correction", "Assign reviewer", "Approve metadata", "Approve geometry",
              "Reject submission", "Escalate conflict", "Send for survey", "Commit to register", "Return to staging"].map((a) => (
              <li key={a} className="flex items-center gap-2"><CheckCircle2 size={12} className="text-[#6BB88A]" />{a}</li>
            ))}
          </ul>
        </div>
      )}

      {/* QUALITY */}
      {tab === "quality" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mm-quality">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Latest quality snapshot</div>
            {dqd?.latest ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {[
                  ["Completeness", dqd.latest.completeness_pct],
                  ["Coordinate accuracy", dqd.latest.coordinate_accuracy_pct],
                  ["Topology integrity", dqd.latest.topology_integrity_pct],
                  ["Validation pass rate", dqd.latest.validation_pass_rate_pct],
                  ["Duplicate rate", dqd.latest.duplicate_rate_pct],
                  ["Confidence average", Math.round((dqd.latest.confidence_avg || 0) * 100)],
                ].map(([k, v]) => (
                  <div key={k} className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                    <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">{k}</div>
                    <div className="text-2xl font-display font-bold text-[#F6F8FA] mt-1">{v}%</div>
                  </div>
                ))}
              </div>
            ) : (<div className="text-[13px] text-[#B3C2CC] italic mt-2">No data</div>)}
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">14-day trend</div>
            <table className="w-full mt-3 text-[12px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Captured", "Complete", "Accuracy", "Topology", "Confidence"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(dqd?.snapshots || []).map((s) => (
                  <tr key={s.id}>
                    <td className="px-2 py-1.5 font-mono-cad text-[10px] text-[#B3C2CC]">{s.captured_at?.slice(0, 10)}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{s.completeness_pct}%</td>
                    <td className="px-2 py-1.5 font-mono-cad">{s.coordinate_accuracy_pct}%</td>
                    <td className="px-2 py-1.5 font-mono-cad">{s.topology_integrity_pct}%</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{Math.round((s.confidence_avg || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GO LIVE */}
      {tab === "go-live" && (
        <div className="cad-panel p-5" data-testid="mm-golive">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Progressive go-live management</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Authorised administrators may transition provinces through the go-live lifecycle. No region moves live without explicit approval.</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Province", "Status", "Readiness %", "Set status"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(goLive.data || []).map((g) => (
                <tr key={g.id} data-testid={`mm-golive-${g.province.replace(/\s+/g, "-")}`}>
                  <td className="px-2 py-1.5 text-[#F6F8FA] flex items-center gap-1.5"><MapPin size={11} className="text-[#C99A2E]" />{g.province}</td>
                  <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{g.status.replace(/_/g, " ")}</span></td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#DCE3E8]">{g.readiness_pct}%</td>
                  <td className="px-2 py-1.5">
                    <select onChange={(e) => setGoLive(g.province, e.target.value)} defaultValue=""
                            data-testid={`mm-golive-set-${g.province.replace(/\s+/g, "-")}`}
                            className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-1 text-[11px] text-[#F6F8FA]">
                      <option value="" disabled>Advance to…</option>
                      {(ref.data?.go_live_statuses || []).map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
